// Dashboard Telesales — 3 góc nhìn theo vai trò:
//  • Quản lý vận hành (Admin, Trưởng phòng, Leader ca): điều hành trực tiếp, việc tồn, phễu, hiệu suất
//  • Nhân viên Telesales: việc cần làm, chỉ tiêu cá nhân, khách theo mã S, lịch đã đặt
//  • Tư vấn cơ sở: khách đến theo lịch → check-in → tư vấn → kết quả (trả ngược về Telesales)
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, InputNumber, Modal, Segmented } from 'antd';
import dayjs from 'dayjs';
import { Headphones, LayoutDashboard, Scale, Settings2, Store } from 'lucide-react';
import useAuthStore from '../../../store/authStore';
import { buildModel } from './data';
import { DEFAULT_CFG, type Cfg, type Ctx } from './logic';
import { useTeleSettingsSync } from '../settingsApi';
import useTeleDash, { type View } from './store';
import OpsView from './OpsView';
import AgentView from './AgentView';
import BranchView from './BranchView';
import BalanceView from './BalanceView';

const WEEKDAY = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];

/** Đồng hồ cập nhật mỗi 30 giây để số liệu trực tiếp tự làm mới */
function useNow(ms = 30_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

const CFG_FIELDS: { name: keyof Cfg; label: string; unit: string; min: number }[] = [
  { name: 'slaSec', label: 'Service level — nhận cuộc gọi đến trong', unit: 'giây', min: 5 },
  { name: 'missedMin', label: 'Gọi lại cuộc nhỡ trong', unit: 'phút', min: 5 },
  { name: 'newLeadMin', label: 'Gọi khách mới trong', unit: 'phút', min: 1 },
  { name: 'forgotDays', label: 'Khách S2–S4 không chạm quá', unit: 'ngày', min: 1 },
  { name: 'acwMaxMin', label: 'Hoàn tất sau cuộc gọi tối đa', unit: 'phút', min: 1 },
  { name: 'callTarget', label: 'Chỉ tiêu cuộc gọi đi / NV / ngày', unit: 'cuộc', min: 1 },
  { name: 'bookTarget', label: 'Chỉ tiêu lịch đặt / NV / ngày', unit: 'lịch', min: 1 },
];

function CfgModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { cfg, setCfg } = useTeleDash();
  const [form] = Form.useForm<Cfg>();
  useEffect(() => { if (open) form.setFieldsValue(cfg); }, [open, cfg, form]);
  return (
    <Modal open={open} title="Ngưỡng vận hành Telesales" okText="Lưu" cancelText="Huỷ" onCancel={onClose}
      onOk={async () => { setCfg(await form.validateFields()); onClose(); }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <div className="flex items-center justify-between">
          <Button type="link" className="!px-0" onClick={() => form.setFieldsValue(DEFAULT_CFG)}>Khôi phục mặc định</Button>
          <span className="flex gap-2"><CancelBtn /><OkBtn /></span>
        </div>
      )}>
      <Form form={form} layout="horizontal" labelCol={{ span: 15 }} labelAlign="left" colon={false} className="pt-2">
        {CFG_FIELDS.map(f => (
          <Form.Item key={f.name} name={f.name} label={f.label} rules={[{ required: true, message: 'Bắt buộc' }]} className="!mb-3">
            <InputNumber min={f.min} className="!w-full" addonAfter={f.unit} />
          </Form.Item>
        ))}
      </Form>
      <p className="text-xs text-gray-500">Ngưỡng dùng để tô đỏ việc trễ, tính Service level và chỉ tiêu trên cả 3 góc nhìn.</p>
    </Modal>
  );
}

export default function TelesalesDashboard() {
  useTeleSettingsSync();
  const { view, set, cfg, appt, done, assign, seed, ensureSeed, agentId } = useTeleDash();
  const { isSuperAdmin, hasPermission } = useAuthStore();
  // Quản lý: Admin hoặc có quyền cấu hình / phân công Telesales
  const canManage = isSuperAdmin || hasPermission('telesales.config') || hasPermission('telesales.assign');
  const [cfgOpen, setCfgOpen] = useState(false);
  const navigate = useNavigate();
  const now = useNow();
  const dateKey = dayjs(now).format('YYYY-MM-DD');

  useEffect(() => { ensureSeed(); }, [dateKey, ensureSeed]);
  useEffect(() => { if ((view === 'ops' || view === 'balance') && !canManage) set({ view: 'agent' }); }, [view, canManage, set]);

  const m = useMemo(() => buildModel(dateKey), [dateKey]);
  const seedAt = seed.date === dateKey ? seed.at : now;
  const ctx: Ctx = useMemo(() => ({ m, now, cfg, ov: { appt, done, assign }, seedAt }), [m, now, cfg, appt, done, assign, seedAt]);

  const views = [
    ...(canManage ? [{ value: 'ops' as View, label: <span className="inline-flex items-center gap-1.5 px-1"><LayoutDashboard size={14} />Quản lý vận hành</span> }] : []),
    { value: 'agent' as View, label: <span className="inline-flex items-center gap-1.5 px-1"><Headphones size={14} />Nhân viên Telesales</span> },
    { value: 'branch' as View, label: <span className="inline-flex items-center gap-1.5 px-1"><Store size={14} />Tư vấn cơ sở</span> },
    ...(canManage ? [{ value: 'balance' as View, label: <span className="inline-flex items-center gap-1.5 px-1"><Scale size={14} />Cân bằng số</span> }] : []),
  ];
  const current = (view === 'ops' || view === 'balance') && !canManage ? 'agent' : view;
  const subtitle = {
    ops: 'Điều hành trực tiếp tổng đài, việc tồn và hiệu quả phễu — dành cho Admin, Trưởng phòng, Leader ca',
    agent: 'Việc cần làm hôm nay, chỉ tiêu cá nhân và khách đang phụ trách',
    branch: 'Khách đến cơ sở theo lịch Telesales đặt: check-in, tư vấn và ghi kết quả',
    balance: 'Số chia cho từng telesale theo nhóm dịch vụ, tỉ lệ yêu cầu và số bù — thay file Excel cân bằng số',
  }[current];

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-4 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Dashboard Telesales</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{WEEKDAY[dayjs(now).day()]}, {dayjs(now).format('DD/MM/YYYY')} · {subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented value={current} onChange={v => set({ view: v as View })} options={views} />
          {canManage && <Button icon={<Settings2 size={15} />} onClick={() => navigate('/telesales/settings?tab=ops')}>Cài đặt</Button>}
        </div>
      </div>

      {current === 'ops' && <OpsView ctx={ctx} onOpenAgent={id => set({ view: 'agent', agentId: id })} />}
      {current === 'agent' && <AgentView key={agentId} ctx={ctx} canPick={canManage} />}
      {current === 'branch' && <BranchView ctx={ctx} />}
      {current === 'balance' && <BalanceView ctx={ctx} />}
      <CfgModal open={cfgOpen} onClose={() => setCfgOpen(false)} />
    </div>
  );
}
